import { logger } from './logger';
import { Join } from './types';
import { interleave } from './utils';
const DEFAULT_GROUP = 'default';
/**
 * Builder class used to generate query criteria
 *
 * @public
 */
export class CriteriaBuilder {
    constructor() {
        this.expressions = {};
        this.And = (expression, options = {}) => this.withExpression(expression, options, Join.And());
        this.Or = (expression, options = {}) => this.withExpression(expression, options, Join.Or());
        this.Not = (expression, options = {}) => this.withExpression(expression, options, Join.Not());
    }
    withExpression(expression, options = {}, groupJoin = Join.And()) {
        const { condition, join: optionsJoin, groupName } = options;
        const addExpressionToGroup = (e, gName = DEFAULT_GROUP) => {
            var _a;
            const prev = (_a = this.expressions[gName]) !== null && _a !== void 0 ? _a : [];
            this.expressions[gName] = [
                ...prev,
                { join: groupJoin || optionsJoin, condition, expression: e },
            ];
        };
        // When expression is array of expressions
        if (Array.isArray(expression)) {
            // And a join option is present
            if (optionsJoin) {
                // Remove inline joins
                const withoutInline = expression.filter((e) => {
                    const isJoin = e instanceof Join;
                    if (isJoin) {
                        logger.warn('Inline joins will be discarded when join is defined in ExpressionOptions');
                    }
                    return !isJoin;
                });
                if (groupName) {
                    // Add every expression to the group
                    withoutInline.forEach((exp) => {
                        addExpressionToGroup(exp, groupName);
                    });
                }
                else {
                    // Merge join specified in options with expressions and add to group
                    const joined = interleave(withoutInline, optionsJoin);
                    addExpressionToGroup(joined, DEFAULT_GROUP);
                }
            }
            else {
                addExpressionToGroup(expression, DEFAULT_GROUP);
            }
        }
        else if (typeof expression === 'string') {
            addExpressionToGroup(expression, DEFAULT_GROUP);
        }
        else {
            addExpressionToGroup(expression, expression.group);
        }
        return this;
    }
    build(outputFn) {
        const parsedExpressions = Object.entries(this.expressions).reduce((acc, [groupName, expressions]) => (Object.assign(Object.assign({}, acc), { [groupName]: `(${expressions
                .filter(({ condition }) => (condition ? condition() : true))
                .map(({ expression, join }, index, arr) => this.parseExpression(expression, arr[index - 1], join))
                .join('')})` })), {});
        return outputFn
            ? outputFn(parsedExpressions)
            : `(${Object.values(parsedExpressions).join(' && ')})`;
    }
    reset() {
        this.expressions = {};
    }
    parseExpression(expression, previous, join) {
        const result = [];
        if (join && !!previous) {
            result.push(` ${join.operator} `);
        }
        if (typeof expression === 'string') {
            result.push(expression);
        }
        else if (Array.isArray(expression)) {
            expression.forEach((e, idx, arr) => {
                if (e instanceof Join) {
                    const prev = arr[idx - 1];
                    if (prev instanceof Join) {
                        throw new Error('Adjacent Join operations detected');
                    }
                }
            });
            result.push(`(${expression.map((e) => this.parseExpression(e, undefined)).join('')})`);
        }
        else if (expression instanceof Join) {
            result.push(` ${expression.operator} `);
        }
        else if (expression) {
            result.push(expression.serialiser(expression));
        }
        return result.join('');
    }
}
